import React, { useEffect, useState } from 'react';
import { Modal, ModalContent, ModalBody, Button, ModalHeader } from '@heroui/react';
import { Icon } from '@iconify/react';
import { ResponsiveModal, Typography } from '../../../../components';
import { CloseButton } from '../../../../components/icons';
import { useTranslations } from '../../../../contexts/translation.context';
import { ProposalData } from '../types';
import PartnerTab from './PartnerDetailsModal/PartnerTab';
import OfferTab from './PartnerDetailsModal/OfferTab';
import OrdersTab from './PartnerDetailsModal/OrdersTab';
import clientCplService, {
  PartnerInfoResponse,
  PartnerOfferInfoResponse,
  PartnerOrdersResponse,
} from '../../../../api/client/clientCplService';

interface PartnerDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  proposal: ProposalData;
}

type TabType = 'partner' | 'offer' | 'orders';

const ORDERS_PAGE_SIZE = 10;

const PartnerDetailsModal: React.FC<PartnerDetailsModalProps> = ({ isOpen, onClose, proposal }) => {
  const { t } = useTranslations();
  const [activeTab, setActiveTab] = useState<TabType>('partner');
  const [partnerInfo, setPartnerInfo] = useState<PartnerInfoResponse | null>(null);
  const [offerInfo, setOfferInfo] = useState<PartnerOfferInfoResponse | null>(null);
  const [ordersInfo, setOrdersInfo] = useState<PartnerOrdersResponse | null>(null);
  const [isPartnerInfoLoading, setIsPartnerInfoLoading] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let isMounted = true;
    setIsPartnerInfoLoading(true);
    setPartnerInfo(null);
    setOfferInfo(null);

    const fetchData = async () => {
      try {
        const [partnerResponse, offerResponse] = await Promise.all([
          clientCplService.getParnterInfo(proposal.partner.id),
          clientCplService.getParnterInfoOffer(proposal.partner.id, proposal.id),
        ]);

        if (!isMounted) return;
        setPartnerInfo(partnerResponse);
        setOfferInfo(offerResponse);
      } catch (error) {
        console.error('Failed to load partner details', error);
      } finally {
        if (isMounted) {
          setIsPartnerInfoLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [isOpen, proposal.id, proposal.partner.id]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let isMounted = true;
    setIsOrdersLoading(true);

    const fetchOrders = async () => {
      try {
        const ordersResponse = await clientCplService.getParnterInfoOrders(proposal.partner.id, {
          page: ordersPage,
          page_size: ORDERS_PAGE_SIZE,
        });

        if (!isMounted) return;
        setOrdersInfo(ordersResponse);
      } catch (error) {
        console.error('Failed to load partner orders', error);
      } finally {
        if (isMounted) {
          setIsOrdersLoading(false);
        }
      }
    };

    fetchOrders();

    return () => {
      isMounted = false;
    };
  }, [isOpen, ordersPage, proposal.partner.id]);

  useEffect(() => {
    if (!isOpen) {
      setOrdersInfo(null);
      setOrdersPage(1);
      return;
    }

    setOrdersInfo(null);
    setOrdersPage(1);
  }, [isOpen, proposal.partner.id]);

  const renderTabIcon = (tab: TabType, isActive: boolean) => {
    const iconColor = isActive ? '#9675CE' : '#71717A';

    switch (tab) {
      case 'partner':
        return (
          <Icon
            icon="solar:user-rounded-bold"
            width={16}
            height={16}
            style={{ color: iconColor }}
          />
        );
      case 'offer':
        return (
          <Icon
            icon="solar:document-text-bold"
            width={16}
            height={16}
            style={{ color: iconColor }}
          />
        );
      case 'orders':
        return (
          <Icon
            icon="solar:cart-large-minimalistic-bold"
            width={16}
            height={16}
            style={{ color: iconColor }}
          />
        );
    }
  };

  const getTabLabel = (tab: TabType) => {
    switch (tab) {
      case 'partner':
        return t('proposals.tabs.partner');
      case 'offer':
        return `${t('proposals.tabs.offer')} #${proposal.internalId}`;
      case 'orders':
        return t('proposals.tabs.orders');
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'partner':
        return (
          <PartnerTab
            proposal={proposal}
            onClose={onClose}
            partnerInfo={partnerInfo}
            isLoading={isPartnerInfoLoading}
          />
        );
      case 'offer':
        return (
          <OfferTab
            proposal={proposal}
            onClose={onClose}
            offerInfo={offerInfo}
            isLoading={isPartnerInfoLoading}
          />
        );
      case 'orders':
        return (
          <OrdersTab
            onClose={onClose}
            ordersInfo={ordersInfo}
            isLoading={isOrdersLoading}
            currentPage={ordersPage}
            pageSize={ORDERS_PAGE_SIZE}
            onPageChange={setOrdersPage}
          />
        );
      default:
        return null;
    }
  };

  const getModalWidth = () => {
    if (activeTab === 'offer') return 'md:max-w-[56.25rem]';
    if (activeTab === 'orders') return 'md:max-w-[59.375rem]';
    return 'max-w-[37.5rem]';
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      classNames={{
        base: getModalWidth(),
        body: 'p-0',
        header: 'p-0',
        footer: 'p-0',
      }}
    >
      <ModalContent className="bg-white border border-light rounded-4">
        <ModalHeader className="p-0">
          <div className="overflow-hidden">
            <div className="px-4 py-5">
              <Typography variant="h6" className="font-medium text-auth-ink">
                {`(#${proposal.internalId}) ${t('proposals.tabs.offer')} ${proposal.partner.name}`}
              </Typography>
            </div>

            {/* Tabs */}
            <div className="border-b border-[#F1E8FF] px-4 flex gap-6 max-md:overflow-x-auto scroller-none">
              {(['partner', 'offer', 'orders'] as TabType[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={` max-sm:shrink-0 flex items-center gap-1 px-1 pb-3 border-b-2 transition-colors ${
                    activeTab === tab ? 'border-primary' : 'border-transparent'
                  }`}
                >
                  {renderTabIcon(tab, activeTab === tab)}
                  <span
                    className={`text-sm font-medium leading-5 ${
                      activeTab === tab ? 'text-primary' : 'text-auth-muted'
                    }`}
                  >
                    {getTabLabel(tab)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </ModalHeader>

        {/* Tab content */}
        <ModalBody className="max-md:max-h-[70vh] max-md:overflow-y-auto">
          <div>{renderTabContent()}</div>
        </ModalBody>
      </ModalContent>
    </ResponsiveModal>
  );
};

export default PartnerDetailsModal;
